import { Suspense } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { AuthProvider, useAuth } from '../contexts/AuthContext'
import { LangProvider, useLang } from '../contexts/LangContext'
import Layout from './layout/Layout'
import Home from '../pages/Home'
import Search from '../pages/Search'
import BookingPage from '../pages/BookingPage'
import CustomerDashboardPage from '../pages/CustomerDashboardPage'
import BookingsListPage from '../pages/BookingsListPage'
import BookingDetailPage from '../pages/BookingDetailPage'
import BookingReviewPage from '../pages/BookingReviewPage'
import PropertiesPage from '../pages/PropertiesPage'
import SettingsPage from '../pages/SettingsPage'
import ProviderProfilePage from '../pages/ProviderProfilePage'
import ProviderDashboardPage from '../pages/ProviderDashboardPage'
import ProviderOnboardingPage from '../pages/ProviderOnboardingPage'
import ProfileEditPage from '../pages/provider/ProfileEditPage'
import ServicesPage from '../pages/provider/ServicesPage'
import AvailabilityPage from '../pages/provider/AvailabilityPage'
import AreasPage from '../pages/provider/AreasPage'
import AdminDashboardPage from '../pages/admin/AdminDashboardPage'
import AdminProvidersPage from '../pages/admin/AdminProvidersPage'
import AdminProviderDetailPage from '../pages/admin/AdminProviderDetailPage'
import AdminBookingsPage from '../pages/admin/AdminBookingsPage'

// Auth pages (eager — small, needed immediately)
import SignIn from '../pages/auth/SignIn'
import SignUp from '../pages/auth/SignUp'
import ProviderSignUp from '../pages/auth/ProviderSignUp'
import ForgotPassword from '../pages/auth/ForgotPassword'
import ResetPassword from '../pages/auth/ResetPassword'

// Placeholder for routes not yet built
function ComingSoon({ label }: { label: string }) {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="text-center">
        <p className="text-slate-400 text-sm font-medium uppercase tracking-wide mb-2">Σύντομα</p>
        <h2 className="text-2xl font-bold text-slate-900">{label}</h2>
      </div>
    </div>
  )
}

// Route guards
function RequireAuth({
  children,
  role,
  adminOnly = false,
}: {
  children: React.ReactNode
  role?: 'customer' | 'provider'
  adminOnly?: boolean
}) {
  const { currentUser, loading } = useAuth()
  const location = useLocation()

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-600 border-t-transparent" /></div>
  if (!currentUser) return <Navigate to="/signin" state={{ from: location.pathname }} replace />
  if (adminOnly && !currentUser.admin) return <Navigate to="/" replace />
  if (role && currentUser.role !== role) return <Navigate to="/" replace />

  return <>{children}</>
}

function RedirectIfAuthed({ children }: { children: React.ReactNode }) {
  const { currentUser } = useAuth()
  if (currentUser?.admin) return <Navigate to="/admin" replace />
  if (currentUser) return <Navigate to={currentUser.role === 'provider' ? '/provider/dashboard' : '/dashboard'} replace />
  return <>{children}</>
}

function AppRoutes() {
  const { t } = useLang()

  return (
    <Layout>
      <Routes>
        {/* Public */}
        <Route path="/" element={<Home />} />
        <Route path="/how-it-works" element={<ComingSoon label={t('Πώς λειτουργεί', 'How it works')} />} />
        <Route path="/search" element={<Search />} />
        <Route path="/providers/:id" element={<ProviderProfilePage />} />

        {/* Auth (redirect if already signed in) */}
        <Route path="/signin" element={<RedirectIfAuthed><SignIn /></RedirectIfAuthed>} />
        <Route path="/signup" element={<RedirectIfAuthed><SignUp /></RedirectIfAuthed>} />
        <Route path="/provider/signup" element={<RedirectIfAuthed><ProviderSignUp /></RedirectIfAuthed>} />
        <Route path="/sign-up/provider" element={<RedirectIfAuthed><ProviderSignUp /></RedirectIfAuthed>} />
        <Route path="/forgot-password" element={<RedirectIfAuthed><ForgotPassword /></RedirectIfAuthed>} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Customer routes */}
        <Route path="/dashboard" element={<RequireAuth role="customer"><CustomerDashboardPage /></RequireAuth>} />
        <Route path="/book" element={<RequireAuth role="customer"><Navigate to="/search" replace /></RequireAuth>} />
        <Route path="/book/:providerProfileId" element={<RequireAuth role="customer"><BookingPage /></RequireAuth>} />
        <Route path="/bookings" element={<RequireAuth role="customer"><BookingsListPage /></RequireAuth>} />
        <Route path="/bookings/:id" element={<RequireAuth role="customer"><BookingDetailPage /></RequireAuth>} />
        <Route path="/bookings/:id/review" element={<RequireAuth role="customer"><BookingReviewPage /></RequireAuth>} />
        <Route path="/properties" element={<RequireAuth role="customer"><PropertiesPage /></RequireAuth>} />
        <Route path="/notifications" element={<RequireAuth><ComingSoon label="Ειδοποιήσεις" /></RequireAuth>} />
        <Route path="/settings" element={<RequireAuth><SettingsPage /></RequireAuth>} />
        <Route path="/profile" element={<RequireAuth><Navigate to="/settings" replace /></RequireAuth>} />

        {/* Provider routes */}
        <Route path="/provider/onboarding" element={<RequireAuth role="provider"><ProviderOnboardingPage /></RequireAuth>} />
        <Route path="/provider/dashboard" element={<RequireAuth role="provider"><ProviderDashboardPage /></RequireAuth>} />
        <Route path="/provider/bookings" element={<RequireAuth role="provider"><ComingSoon label="Κρατήσεις" /></RequireAuth>} />
        <Route path="/provider/availability" element={<RequireAuth role="provider"><AvailabilityPage /></RequireAuth>} />
        <Route path="/provider/services" element={<RequireAuth role="provider"><ServicesPage /></RequireAuth>} />
        <Route path="/provider/areas" element={<RequireAuth role="provider"><AreasPage /></RequireAuth>} />
        <Route path="/provider/profile" element={<RequireAuth role="provider"><ProfileEditPage /></RequireAuth>} />
        <Route path="/provider/wallet" element={<RequireAuth role="provider"><ComingSoon label="Πορτοφόλι" /></RequireAuth>} />

        {/* Admin routes */}
        <Route path="/admin" element={<RequireAuth adminOnly><AdminDashboardPage /></RequireAuth>} />
        <Route path="/admin/providers" element={<RequireAuth adminOnly><AdminProvidersPage /></RequireAuth>} />
        <Route path="/admin/providers/:id" element={<RequireAuth adminOnly><AdminProviderDetailPage /></RequireAuth>} />
        <Route path="/admin/bookings" element={<RequireAuth adminOnly><AdminBookingsPage /></RequireAuth>} />
        <Route path="/admin/reviews" element={<RequireAuth adminOnly><Navigate to="/admin" replace /></RequireAuth>} />

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  )
}

export default function App() {
  return (
    <LangProvider>
      <AuthProvider>
        <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-600 border-t-transparent" /></div>}>
          <AppRoutes />
        </Suspense>
      </AuthProvider>
    </LangProvider>
  )
}
